import { useState, useCallback, useRef, useEffect } from "react";

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correct_index: number;
  explanation: string;
}

interface QuizProps {
  questions: QuizQuestion[];
  onQuizFinish?: (passed: boolean) => void;
  onRetest?: () => void;
}

const PASS_THRESHOLD = 60;

export default function Quiz({ questions, onQuizFinish, onRetest }: QuizProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [answers, setAnswers] = useState<(number | null)[]>(
    new Array(questions.length).fill(null)
  );
  const answerAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentIndex(0);
    setSelectedOption(null);
    setAnswered(false);
    setScore(0);
    setFinished(false);
    setAnswers(new Array(questions.length).fill(null));
  }, [questions]);

  useEffect(() => {
    if (answered && answerAreaRef.current) {
      setTimeout(() => {
        answerAreaRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
    }
  }, [answered]);

  const handleSelect = useCallback(
    (optionIndex: number) => {
      if (answered) return;
      setSelectedOption(optionIndex);
      setAnswered(true);
      const newAnswers = [...answers];
      newAnswers[currentIndex] = optionIndex;
      setAnswers(newAnswers);
      if (optionIndex === questions[currentIndex].correct_index) {
        setScore((s) => s + 1);
      }
    },
    [answered, answers, currentIndex, questions]
  );

  const goNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((i) => i + 1);
      setSelectedOption(null);
      setAnswered(false);
    } else {
      const pct = Math.round((score / questions.length) * 100);
      const passed = pct >= PASS_THRESHOLD;
      setFinished(true);
      onQuizFinish?.(passed);
    }
  };

  const retry = () => {
    setCurrentIndex(0);
    setSelectedOption(null);
    setAnswered(false);
    setScore(0);
    setFinished(false);
    setAnswers(new Array(questions.length).fill(null));
  };

  if (questions.length === 0)
    return <p className="empty-msg">No quiz questions available</p>;

  if (finished) {
    const pct = Math.round((score / questions.length) * 100);
    const passed = pct >= PASS_THRESHOLD;
    return (
      <div className="quiz-result">
        <div className={`quiz-score-circle ${passed ? "" : "fail"}`}>
          <span className="quiz-score-number">{pct}%</span>
        </div>
        <p className="quiz-score-text">
          You got <strong>{score}</strong> out of <strong>{questions.length}</strong> correct
        </p>
        {passed ? (
          <p className="quiz-pass-msg">You passed!</p>
        ) : (
          <p className="quiz-fail-msg">
            You need at least {PASS_THRESHOLD}% to pass. Try again!
          </p>
        )}
        {passed ? (
          <button className="quiz-retry-btn" onClick={retry}>
            Retry Quiz
          </button>
        ) : (
          <button className="quiz-retest-btn" onClick={() => { retry(); onRetest?.(); }}>
            Retest with New Questions
          </button>
        )}
      </div>
    );
  }

  const q = questions[currentIndex];

  return (
    <div className="quiz-container">
      <div className="quiz-question-box">
        <div className="quiz-header">
          <span className="quiz-counter">
            Question {currentIndex + 1} / {questions.length}
          </span>
          <span className="quiz-score-inline">Score: {score}</span>
        </div>

        <div className="question-text">{q.question}</div>
      </div>

      <div className="quiz-options">
        {q.options.map((opt, i) => {
          let cls = "quiz-option";
          if (answered) {
            if (i === q.correct_index) cls += " correct";
            else if (i === selectedOption) cls += " incorrect";
          } else if (i === selectedOption) {
            cls += " selected";
          }
          return (
            <button key={i} className={cls} onClick={() => handleSelect(i)}>
              <span className="option-letter">
                {String.fromCharCode(65 + i)}
              </span>
              <span className="option-text">{opt}</span>
            </button>
          );
        })}
      </div>

      <div className="quiz-answer-area" ref={answerAreaRef}>
        {answered && q.explanation && (
          <div className="quiz-explanation">
            <strong>Explanation:</strong> {q.explanation}
          </div>
        )}

        {answered && (
          <button className="quiz-next-btn" onClick={goNext}>
            {currentIndex === questions.length - 1 ? "See Results" : "Next"}
          </button>
        )}
      </div>
    </div>
  );
}
